import type { ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import type { Chart as ChartJS } from 'chart.js';

import {
    buildMetricChartData,
    buildMetricChartOptions,
    MetricChart,
    type MetricChartSpec,
} from '@/components/charts/generic';
import { useChartTheme } from '@/components/charts/theme';

export interface CompareLineChartProps {
    spec: MetricChartSpec<'line'>;
    chartHeight?: number;
    isLoading?: boolean;
    /** The page's cage legend, which owns which cages are shown. */
    legend?: ReactNode;
    chartRef: RefObject<ChartJS<'line'> | null>;
    /** Already translated: the metric on show, which names the chart for screen readers. */
    title?: string;
}

const NO_LEGEND_ITEMS: never[] = [];

/**
 * The Compare page's chart: a MetricChart whose series visibility belongs to the page's cage
 * legend, so it is built from the spec directly instead of through `useMetricChart`.
 */
export default function CompareLineChart(props: CompareLineChartProps) {
    const { spec, chartHeight = 400, isLoading = false, legend, chartRef, title } = props;
    const theme = useChartTheme();

    const data = useMemo(() => buildMetricChartData(spec, theme), [spec, theme]);
    const options = useMemo(() => buildMetricChartOptions(spec, theme), [spec, theme]);
    const visibleSeries = useMemo(() => new Set(spec.series.map((_, index) => index)), [spec]);

    return (
        <MetricChart
            kind="line"
            data={data}
            options={options}
            legendItems={NO_LEGEND_ITEMS}
            visibleSeries={visibleSeries}
            onToggleSeries={() => undefined}
            chartRef={chartRef}
            height={chartHeight}
            isLoading={isLoading}
            legend={legend}
            title={title}
        />
    );
}
